import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMedia } from "./MediaContext";

import {
    useAnnotationsForFileAndFrameId,
    usePredictionsForFileAndFrameSegment,
    useAnnotationsForFile,
    usePredictionsForFile,
    useSetAnnotationsForFileAndFrameId,
    useSetPredictionsForFileAndFrameSegment,
    useClearAnnotationsForFile,
    useClearPredictionsForFile,
    useClearPredictionsForFileAndFrameSegment,
    useSyncPredictionsAndAnnotationWithMediaItems,
    useHasAnnotations,
    useHasPredictions,
    usePromptLabels,
    useSetPromptLabels,
    useSyncPromptLabels,
    useSetPromptLabelsGlobalConfidenceThreshold,
    useUpdatePromptLabels,
    useCurrentMedia,
    useClearReviewedFrames
} from "@rapid/store";
import { ModelContext, ModelContextType } from "./ModelContext";
import { MediaItemStatus } from "@rapid/types/media";

export const ModelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const media = useMedia();

    // Get state from Zustand store
    const annotationsForFileAndFrameId = useAnnotationsForFileAndFrameId();
    const predictionsForFileAndFrameSegment = usePredictionsForFileAndFrameSegment();
    const promptLabels = usePromptLabels();
    const hasAnnotations = useHasAnnotations();
    const hasPredictions = useHasPredictions();
    const currentMedia = useCurrentMedia();

    // Get actions from Zustand store
    const setAnnotationsForFileAndFrameId = useSetAnnotationsForFileAndFrameId();
    const setPredictionsForFileAndFrameSegment = useSetPredictionsForFileAndFrameSegment();
    const syncPredictionsAndAnnotationWithMediaItems =
        useSyncPredictionsAndAnnotationWithMediaItems();
    const setPromptLabels = useSetPromptLabels();
    const updatePromptLabels = useUpdatePromptLabels();
    const syncPromptLabels = useSyncPromptLabels();
    const setPromptLabelsGlobalConfidenceThreshold = useSetPromptLabelsGlobalConfidenceThreshold();

    // Get computed data from Zustand store
    const annotationsForFile = useAnnotationsForFile(currentMedia?.file.id);
    const predictionsForFile = usePredictionsForFile(currentMedia?.file.id);

    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [isTestingNewFiles, setIsTestingNewFiles] = useState(false);
    const [testingProgress, setTestingProgress] = useState(0);
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [approvedFileIds, setApprovedFileIds] = useState<Set<string>>(new Set());

    // === text prompt ===
    const [textPromptStr, setTextPromptStr] = useState("");
    const textPrompts = useMemo(() => {
        if (!textPromptStr) {
            return [];
        }
        const prompts = textPromptStr
            .split(/[^A-Za-z0-9\s-]+/)
            .map((prompt) => prompt.trim().replace(/^[\s-]+|[\s-]+$/g, ""))
            .filter((prompt) => prompt.length > 0);

        return Array.from(new Set(prompts)).slice(0, 5);
    }, [textPromptStr]);

    // === helpers ===
    const clearAnnotationsForFile = useClearAnnotationsForFile();
    const clearPredictionsForFile = useClearPredictionsForFile();
    const clearPredictionsForFileAndFrameSegment = useClearPredictionsForFileAndFrameSegment();
    const clearReviewedFrames = useClearReviewedFrames();

    // === approved files management ===
    const markFileAsApproved = useCallback((fileId: string) => {
        setApprovedFileIds((prev) => new Set(prev).add(fileId));
    }, []);

    const isFileApproved = useCallback(
        (fileId: string) => {
            return approvedFileIds.has(fileId);
        },
        [approvedFileIds]
    );

    const lastSyncedMediaItems = useRef<Set<string>>(new Set());

    // Sync media items with the Zustand store
    useEffect(() => {
        const mediaItemsToSync = media.mediaItems.filter(
            (item) => item.status !== MediaItemStatus.DELETED
        );

        if (
            lastSyncedMediaItems.current.size === mediaItemsToSync.length &&
            mediaItemsToSync.every((item) => lastSyncedMediaItems.current.has(item.id))
        )
            return;

        lastSyncedMediaItems.current = new Set(mediaItemsToSync.map((item) => item.id));

        syncPredictionsAndAnnotationWithMediaItems(mediaItemsToSync);
        syncPromptLabels();
    }, [media.mediaItems, syncPredictionsAndAnnotationWithMediaItems, syncPromptLabels]);

    useEffect(() => {
        syncPromptLabels();
    }, [syncPromptLabels, annotationsForFileAndFrameId, predictionsForFileAndFrameSegment]);

    const value = useMemo<ModelContextType>(
        () => ({
            annotationsForFileAndFrameId,
            predictionsForFileAndFrameSegment,
            promptLabels,
            approvedFileIds,
            annotationsForFile,
            predictionsForFile,
            hasAnnotations,
            hasPredictions,
            isDownloading,
            downloadProgress,
            isTestingNewFiles,
            testingProgress,
            abortController,
            textPromptStr,
            textPrompts,
            setAnnotationsForFileAndFrameId,
            setPredictionsForFileAndFrameSegment,
            setPromptLabels,
            updatePromptLabels,
            markFileAsApproved,
            isFileApproved,
            clearAnnotationsForFile,
            clearPredictionsForFile,
            clearPredictionsForFileAndFrameSegment,
            clearReviewedFrames,
            setIsDownloading,
            setDownloadProgress,
            setIsTestingNewFiles,
            setTestingProgress,
            setAbortController,
            setTextPromptStr,
            setPromptLabelsGlobalConfidenceThreshold
        }),
        [
            annotationsForFileAndFrameId,
            predictionsForFileAndFrameSegment,
            promptLabels,
            approvedFileIds,
            annotationsForFile,
            predictionsForFile,
            hasAnnotations,
            hasPredictions,
            isDownloading,
            downloadProgress,
            isTestingNewFiles,
            testingProgress,
            abortController,
            textPromptStr,
            textPrompts,
            setAnnotationsForFileAndFrameId,
            setPredictionsForFileAndFrameSegment,
            setPromptLabels,
            updatePromptLabels,
            markFileAsApproved,
            isFileApproved,
            clearAnnotationsForFile,
            clearPredictionsForFile,
            clearPredictionsForFileAndFrameSegment,
            clearReviewedFrames,
            setIsDownloading,
            setDownloadProgress,
            setIsTestingNewFiles,
            setTestingProgress,
            setAbortController,
            setTextPromptStr,
            setPromptLabelsGlobalConfidenceThreshold
        ]
    );

    return <ModelContext.Provider value={value}>{children}</ModelContext.Provider>;
};
